import { DEBUG_MAP, DEBUG_MODE, apiOrigin, copy, countCopy, debugPanel, formatCopy, state } from "./runtime.js";
import { loadMapboxGl, mapboxToken, mapFallbackMarkup } from "../mapbox-client.js";
import { ARIZONA_BOUNDS, LOCAL_ARIZONA_COUNTIES_URL, LOCAL_ARIZONA_ZCTAS_URL, MAP_LAB_DYNAMIC_SOURCE_ID, MAP_LAB_METRIC_NOTE_LABEL_KEYS, MAP_LAB_PIE_LAYER_ID, mapInstances } from "./constants.js";
import { flooredZoom, getCategoryClusterExpansionZoom } from "./clustering.js";
import { mapLabActiveSymptoms, mapLabDotsVisible, mapLabDrillCounty, mapLabMagnitudeMetric, mapLabShadeLevel, reportCountsByZip, reportMapTimePeriodText } from "./data.js";
import { addCountyLayers, addMapLabDynamicPointLayers, addOutsideArizonaMask, addZipLayers, applyMapLabVisibilityToMap, buildMapLabBaseData, buildMapLabFeatureCollectionForZoom, drillIntoCounty, setMapLabDynamicSourceData } from "./layers.js";
import { pointReportPopupHtml, popupAnchor, wireZipMarkerPopup, zipPolygonPopupHtml } from "./popups.js";
import { symptomListLabel } from "../../symptom-labels.js";

export let mapResizeObserver = null;

let mapSourceUpdateGeneration = 0;

let latestMapLabBaseData = null;

const mapRenderState = new WeakMap();

const INITIAL_MAP_ZOOM = 5.65;

const REPORT_MAP_PROJECTION = "mercator";

const GENERATED_CLUSTER_ICON_STROKE_COLOR = "#ffffff";

const GENERATED_CLUSTER_ICON_STROKE_WIDTH = 1.2;

const GENERATED_CLUSTER_ICON_PIXEL_RATIO = 2;

const GENERATED_CLUSTER_ICON_MAX_IMAGES = 256;

const generatedClusterIconRegistries = new WeakMap();

export function renderReportMaps() {
  updateMapCounts();
  updateMapDebugPanels();
  observeReportMapSizes();
  mapInstances.forEach((map, id) => {
    if (!map.getContainer().isConnected) {
      map.remove();
      mapInstances.delete(id);
    }
  });
  const mapIds = ["frontDoorReportMap", "mobileReportMap"];
  if (DEBUG_MODE && !debugPanel.hidden) mapIds.push("reportMap");
  mapIds.forEach((id) => {
    const element = document.getElementById(id);
    if (!element) return;
    const existingMap = mapInstances.get(id);
    if (existingMap && existingMap.getContainer().isConnected) return;
    if (existingMap) {
      existingMap.remove();
      mapInstances.delete(id);
    }
    initializeReportMap(id).catch(() => {
      element.innerHTML = mapFallbackMarkup(copy("map_load_failed"), copy("map_unavailable_detail"));
    });
  });
  resizeReportMaps();
}


// Flatten Mapbox's light-v11 base style down to a quiet canvas: the built-in road/building/
// landuse sublayers each render slightly differently, which competes with the data dots and
// choropleth on top. We recolor the background layer and hide the noisy non-symbol layers,
// keeping symbol (label) layers so city/highway names stay for orientation.
//
// A short list of non-symbol layers survives: lakes/rivers, parks and other landcover,
// interstates (light-v11 folds all roads into "road-simple", which draws motorways and
// trunks only at our zoom), and the admin-* boundary lines. They are what gives the area
// around Arizona a readable geographic frame once the spotlight mask is only partially
// opaque — without them, lifting the mask would just reveal more blank off-white. Inside
// Arizona they are invisible anyway: the opaque state fill of the mask covers them, so
// this only affects the neighboring states. Buildings, airports, bridges/tunnels, rail,
// and footpaths stay hidden — pure noise at a statewide zoom.
//
// Called before the app's own layers are added, so it only touches Mapbox's built-in base
// layers.
const FLAT_BASE_COLOR = "#f4f4f2"; // off-white / light gray

const BASE_CONTEXT_LAYER_IDS = new Set(["water", "waterway", "national-park", "landuse", "road-simple"]);

const BASE_CONTEXT_LAYER_PREFIX = "admin-";

// light-v11 draws water and boundaries in near-white grays (water is hsl(220, 1%, 86%)),
// which all but disappear once the spotlight veil is composited over them. We nudge the
// two shapes that carry the most orientation — water bodies and state/country lines —
// toward cooler, slightly darker tones so they survive the veil.
const BASE_CONTEXT_WATER_COLOR = "#d5dee6";

const BASE_CONTEXT_BOUNDARY_COLOR = "#9aa3ad";

function isBaseContextLayer(layerId) {
  return BASE_CONTEXT_LAYER_IDS.has(layerId) || layerId.startsWith(BASE_CONTEXT_LAYER_PREFIX);
}

function paintBaseContextLayer(map, layer) {
  if (layer.id === "water") map.setPaintProperty(layer.id, "fill-color", BASE_CONTEXT_WATER_COLOR);
  else if (layer.id === "waterway") map.setPaintProperty(layer.id, "line-color", BASE_CONTEXT_WATER_COLOR);
  // Skip the "-bg" casing layers: those are the soft halo drawn under a boundary, and
  // tinting them turns a crisp state line into a thick smudge.
  else if (layer.id.startsWith(BASE_CONTEXT_LAYER_PREFIX) && layer.type === "line" && !layer.id.endsWith("-bg")) {
    map.setPaintProperty(layer.id, "line-color", BASE_CONTEXT_BOUNDARY_COLOR);
  }
}

function flattenBaseMap(map, color) {
  const layers = map.getStyle().layers || [];
  for (const layer of layers) {
    if (layer.type === "background") {
      map.setPaintProperty(layer.id, "background-color", color);
    } else if (layer.type === "symbol") {
      continue;
    } else if (isBaseContextLayer(layer.id)) {
      paintBaseContextLayer(map, layer);
    } else {
      map.setLayoutProperty(layer.id, "visibility", "none");
    }
  }
}


export async function initializeReportMap(id) {
  const token = mapboxToken();
  const element = document.getElementById(id);
  if (!element) return;

  if (!token) {
    element.innerHTML = mapFallbackMarkup(copy("map_token_needed"), copy("map_token_help"));
    return;
  }

  element.innerHTML = `<div class="map-fallback"><strong>${copy("map_loading")}</strong></div>`;
  await loadMapboxGl();
  element.innerHTML = "";
  window.mapboxgl.accessToken = token;
  const map = new window.mapboxgl.Map({
    container: id,
    style: "mapbox://styles/mapbox/light-v11",
    projection: REPORT_MAP_PROJECTION,
    center: [-111.9, 34.25],
    zoom: INITIAL_MAP_ZOOM,
    maxBounds: [
      [-117.3, 29.8],
      [-106.7, 38.4],
    ],
    attributionControl: false,
    preserveDrawingBuffer: true,
  });
  mapInstances.set(id, map);
  updateMapZoomDebug(map);
  map.on("zoom", () => updateMapZoomDebug(map));

  map.addControl(new window.mapboxgl.AttributionControl({ compact: true }), "bottom-right");

  map.on("load", async () => {
    fitArizonaBounds(map);
    flattenBaseMap(map, FLAT_BASE_COLOR);
    await addOutsideArizonaMask(map);
    await addZipLayers(map);
    await addCountyLayers(map);
    addMapLabDynamicPointLayers(map);
    await updateReportMapSources();
    applyMapLabVisibilityToMap(map);
    wireMapLabZoomRendering(map);

    ["people-clusters", "animals-clusters", "environment-clusters"].forEach((layerId) => {
      wireManualClusterZoom(map, layerId);
    });
    [MAP_LAB_PIE_LAYER_ID, "people-cluster-single-points", "community-report-dot-points"].forEach((layerId) => {
      wireZipMarkerPopup(map, layerId);
    });
    ["animals-cluster-single-points", "animals-points", "environment-cluster-single-points", "environment-points"].forEach((layerId) => {
      map.on("click", layerId, (event) => {
        const feature = event.features && event.features[0];
        if (!feature || !window.mapboxgl) return;
        new window.mapboxgl.Popup({ closeButton: true, maxWidth: "220px" })
          .setLngLat(popupAnchor(feature, event))
          .setHTML(pointReportPopupHtml(feature.properties))
          .addTo(map);
      });
      map.on("mouseenter", layerId, () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", layerId, () => {
        map.getCanvas().style.cursor = "";
      });
    });
    // PREVIEW (Map Lab ZIP shading): click a shaded ZIP polygon for its full read —
    // people counts, per-capita, and the cross-domain animal/environment line.
    map.on("click", "arizona-zip-fills", (event) => {
      const feature = event.features && event.features[0];
      if (!feature || !window.mapboxgl) return;
      new window.mapboxgl.Popup({ closeButton: true, maxWidth: "260px" })
        .setLngLat(event.lngLat)
        .setHTML(zipPolygonPopupHtml(feature.properties))
        .addTo(map);
    });
    map.on("mouseenter", "arizona-zip-fills", () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "arizona-zip-fills", () => {
      map.getCanvas().style.cursor = "";
    });
    // PREVIEW (Map Lab county shading): click a county to drill into its ZIP shading.
    // Only the front-door map has the Area toggle + breadcrumb to navigate back, so the
    // drill interaction is scoped to it; other maps show county shading read-only.
    if (id === "frontDoorReportMap") {
      map.on("click", "arizona-county-fills", (event) => {
        const feature = event.features && event.features[0];
        if (!feature) return;
        drillIntoCounty(map, feature.properties?.county_name || feature.properties?.NAME || "");
      });
      map.on("mouseenter", "arizona-county-fills", () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", "arizona-county-fills", () => {
        map.getCanvas().style.cursor = "";
      });
    }
    exposeMapDebugApi();
    resizeReportMaps();
    logMapDebugSnapshot(id);
  });
}


// ZIP search: the enriched polygon properties for a ZIP (counts, population, per-capita
// norms) as currently rendered, so a searched popup matches a clicked one under the active
// time window and symptom filter. Reads the same base data the dynamic source was built
// from; returns null before the first source build (caller falls back to raw geometry).
export function mapLabZipFeatureProps(zipCode) {
  const zipFeatures = latestMapLabBaseData?.zipFeatures || [];
  const match = zipFeatures.find((feature) => {
    const properties = feature.properties || {};
    return (properties.GEOID || properties.ZCTA5) === zipCode;
  });
  return match ? match.properties : null;
}


export function fitArizonaBounds(map) {
  const zoomBoost = map.getContainer().id === "frontDoorReportMap" ? 0.15 : 0.35;
  map.fitBounds(ARIZONA_BOUNDS, { padding: 8, duration: 0 });
  map.zoomTo(map.getZoom() + zoomBoost, { duration: 0 });
  updateMapZoomDebug(map);
}


export function updateMapZoomDebug(map) {
  const mapId = map.getContainer().id;
  document.querySelectorAll(`[data-map-debug-zoom-for="${mapId}"] [data-map-debug-zoom]`).forEach((node) => {
    node.textContent = map.getZoom().toFixed(2);
  });
}


export function observeReportMapSizes() {
  if (!("ResizeObserver" in window)) return;
  if (!mapResizeObserver) {
    mapResizeObserver = new window.ResizeObserver(() => resizeReportMaps());
  }
  document.querySelectorAll(".report-map").forEach((mapElement) => {
    mapResizeObserver.observe(mapElement);
  });
}


export function resizeReportMaps() {
  window.requestAnimationFrame(() => {
    mapInstances.forEach((map) => {
      if (!map.getContainer().isConnected) return;
      map.resize();
      if (map.getContainer().id === "frontDoorReportMap") fitArizonaBounds(map);
    });
  });
}


export { loadMapboxGl, mapboxToken, mapFallbackMarkup };


export async function updateReportMapSources() {
  updateMapCounts();
  updateMapDebugPanels();
  const updateGeneration = ++mapSourceUpdateGeneration;
  const baseData = await buildMapLabBaseData({ baseGeneration: updateGeneration });
  if (updateGeneration !== mapSourceUpdateGeneration) return;
  const maps = Array.from(mapInstances.values()).filter((map) => map.getContainer().isConnected);
  maps.forEach((map) => {
    mapRenderStateFor(map).generation += 1;
  });
  const preparedRenders = maps.map((map) => ({
    map,
    featureCollection: buildMapLabFeatureCollectionForZoom(baseData, map.getZoom()),
  }));
  latestMapLabBaseData = baseData;
  let setDataCalls = 0;
  preparedRenders.forEach(({ map, featureCollection }) => {
    const setDataResult = setPreparedMapLabDynamicSourceData(map, featureCollection);
    setDataCalls += setDataResult;
    if (setDataResult) recordMapRenderSuccess(map, baseData);
  });
  logMapTimeUpdate(updateGeneration, preparedRenders, setDataCalls);
  exposeMapDebugApi();
}


export function logMapTimeUpdate(updateGeneration, preparedRenders, setDataCalls) {
  const renderCounts = preparedRenders.map(({ map, featureCollection }) => ({
    mapId: map.getContainer().id,
    zoom: Number(map.getZoom().toFixed(2)),
    ...featureCounts(featureCollection),
  }));
  const totals = renderCounts.reduce(
    (accumulator, counts) => ({
      polygonFeatures: accumulator.polygonFeatures + counts.polygonFeatures,
      pointFeatures: accumulator.pointFeatures + counts.pointFeatures,
    }),
    { polygonFeatures: 0, pointFeatures: 0 }
  );
  console.info("[community-report] map time update", {
    generation: updateGeneration,
    timeWindow: reportMapTimePeriodText(),
    setDataCalls,
    renders: renderCounts,
    ...totals,
  });
}


function featureCounts(featureCollection) {
  const features = featureCollection.features || [];
  const polygonFeatures = features.filter((feature) => {
    const type = feature.geometry?.type;
    return type === "Polygon" || type === "MultiPolygon";
  }).length;
  const pointFeatures = features.filter((feature) => feature.geometry?.type === "Point").length;
  return {
    polygonFeatures,
    pointFeatures,
  };
}


function setPreparedMapLabDynamicSourceData(map, featureCollection) {
  return setMapLabDynamicSourceData(map, prepareClusterIconFeatureCollection(map, featureCollection));
}


function prepareClusterIconFeatureCollection(map, featureCollection) {
  return {
    type: "FeatureCollection",
    features: (featureCollection.features || []).map((feature) => {
      if (!isClusterFeature(feature)) return feature;
      const clusterIconKey = clusterIconImageKey(feature);
      const registeredClusterIconKey = registerClusterIconImage(map, feature, clusterIconKey);
      return {
        ...feature,
        properties: {
          ...(feature.properties || {}),
          clusterIconKey: registeredClusterIconKey,
        },
      };
    }),
  };
}


function isClusterFeature(feature) {
  return feature.geometry?.type === "Point" && String(feature.properties?.kind || "").endsWith("-cluster");
}


function clusterIconImageKey(feature) {
  const style = clusterIconStyle(feature);
  return [
    "map-lab-cluster-circle",
    `r${formatIconNumber(style.radius)}`,
    `c${sanitizeIconKeyPart(style.fillColor)}`,
    `o${formatIconNumber(style.fillOpacity)}`,
    `s${formatIconNumber(style.strokeWidth)}`,
  ].join("-");
}


function clusterIconStyle(feature) {
  return {
    radius: positiveNumber(feature.properties?.radius, 6),
    fillColor: String(feature.properties?.dotColor || "#64748b"),
    fillOpacity: clampNumber(feature.properties?.dotOpacity, 0, 1, 0.9),
    strokeColor: GENERATED_CLUSTER_ICON_STROKE_COLOR,
    strokeWidth: GENERATED_CLUSTER_ICON_STROKE_WIDTH,
  };
}


function registerClusterIconImage(map, feature, imageId) {
  if (!map || map.hasImage?.(imageId)) return imageId;
  const registry = generatedClusterIconRegistryFor(map);
  if (registry.has(imageId)) return imageId;
  const style = clusterIconStyle(feature);
  const cappedImageId = registry.size >= GENERATED_CLUSTER_ICON_MAX_IMAGES
    ? fallbackClusterIconImageId(style)
    : imageId;
  if (map.hasImage?.(cappedImageId) || registry.has(cappedImageId)) return cappedImageId;
  map.addImage(cappedImageId, clusterIconImageData(style), {
    pixelRatio: GENERATED_CLUSTER_ICON_PIXEL_RATIO,
  });
  registry.add(cappedImageId);
  return cappedImageId;
}


function fallbackClusterIconImageId(style) {
  return [
    "map-lab-cluster-circle-overflow",
    `s${formatIconNumber(style.strokeWidth)}`,
  ].join("-");
}


function clusterIconImageData(style) {
  const pixelRatio = GENERATED_CLUSTER_ICON_PIXEL_RATIO;
  const radiusPx = style.radius * pixelRatio;
  const strokeWidthPx = style.strokeWidth * pixelRatio;
  const paddingPx = Math.ceil(strokeWidthPx) + 2;
  const size = Math.ceil((radiusPx + strokeWidthPx) * 2 + paddingPx * 2);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  const center = size / 2;

  context.clearRect(0, 0, size, size);
  context.beginPath();
  context.arc(center, center, radiusPx, 0, Math.PI * 2);
  context.globalAlpha = style.fillOpacity;
  context.fillStyle = style.fillColor;
  context.fill();
  context.globalAlpha = 1;
  context.lineWidth = strokeWidthPx;
  context.strokeStyle = style.strokeColor;
  context.stroke();
  return context.getImageData(0, 0, size, size);
}


function generatedClusterIconRegistryFor(map) {
  let registry = generatedClusterIconRegistries.get(map);
  if (!registry) {
    registry = new Set();
    generatedClusterIconRegistries.set(map, registry);
  }
  return registry;
}


function positiveNumber(value, fallback) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback;
}


function clampNumber(value, min, max, fallback) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return fallback;
  return Math.min(max, Math.max(min, numberValue));
}


function formatIconNumber(value) {
  return String(Math.round(Number(value) * 100) / 100).replace(".", "p");
}


function sanitizeIconKeyPart(value) {
  return String(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
}


function mapRenderStateFor(map) {
  let state = mapRenderState.get(map);
  if (!state) {
    state = {
      generation: 0,
      clusterZoom: null,
      baseGeneration: null,
      wiredZoomEnd: false,
    };
    mapRenderState.set(map, state);
  }
  return state;
}


function recordMapRenderSuccess(map, baseData) {
  const state = mapRenderStateFor(map);
  state.clusterZoom = flooredZoom(map.getZoom());
  state.baseGeneration = baseData.baseGeneration;
}


function wireMapLabZoomRendering(map) {
  const state = mapRenderStateFor(map);
  if (state.wiredZoomEnd) return;
  state.wiredZoomEnd = true;
  map.on("zoomend", () => renderMapLabForCurrentZoom(map));
}


function renderMapLabForCurrentZoom(map) {
  const baseData = latestMapLabBaseData;
  if (!baseData || !map.getContainer().isConnected) return;
  const state = mapRenderStateFor(map);
  const clusterZoom = flooredZoom(map.getZoom());
  if (state.clusterZoom === clusterZoom && state.baseGeneration === baseData.baseGeneration) return;
  const renderGeneration = ++state.generation;
  const featureCollection = buildMapLabFeatureCollectionForZoom(baseData, map.getZoom());
  if (renderGeneration !== state.generation) return;
  if (baseData !== latestMapLabBaseData || baseData.baseGeneration !== latestMapLabBaseData?.baseGeneration) return;
  const setDataResult = setPreparedMapLabDynamicSourceData(map, featureCollection);
  if (setDataResult) recordMapRenderSuccess(map, baseData);
}


export function wireManualClusterZoom(map, layerId) {
  map.on("click", layerId, (event) => {
    const feature = event.features && event.features[0];
    const coordinates = feature?.geometry?.coordinates;
    if (!feature || !Array.isArray(coordinates)) return;
    const fallbackZoom = map.getZoom() + 1.6;
    const expansionZoom = clusterExpansionZoomForFeature(feature, map.getZoom());
    map.easeTo({
      center: coordinates,
      zoom: Math.min(map.getMaxZoom(), expansionZoom ?? fallbackZoom),
      duration: 450,
    });
  });
  map.on("mouseenter", layerId, () => {
    map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", layerId, () => {
    map.getCanvas().style.cursor = "";
  });
}


function clusterExpansionZoomForFeature(feature, currentZoom) {
  const baseData = latestMapLabBaseData;
  if (!baseData) return null;
  const properties = feature.properties || {};
  const category = properties.clusterCategory;
  const clusterId = Number(properties.clusterId);
  const clusterBaseGeneration = Number(properties.clusterBaseGeneration);
  const categoryIndex = baseData.clusterIndexes?.[category];
  if (!category || !Number.isFinite(clusterId) || !Number.isFinite(clusterBaseGeneration)) return null;
  if (clusterBaseGeneration !== baseData.baseGeneration || !categoryIndex) return null;
  try {
    const expansionZoom = getCategoryClusterExpansionZoom(categoryIndex, clusterId);
    if (!Number.isFinite(expansionZoom) || expansionZoom <= currentZoom) return null;
    return expansionZoom;
  } catch {
    return null;
  }
}


export function updateMapCounts() {
  const summary = state.communityReportSummary;
  const zipCounts = reportCountsByZip();
  const sickCount = Number.isInteger(summary?.sick_count)
    ? summary.sick_count
    : zipCounts.reduce((total, zipCount) => total + zipCount.sick_count, 0);
  const totalCount = Number.isInteger(summary?.total_count) ? summary.total_count : null;
  const categoryTotals = state.mapLabCategoryTotals || {};
  const allReportCount = Number.isInteger(categoryTotals.total) ? categoryTotals.total : totalCount;
  const zipCount = Number.isInteger(categoryTotals.zipCount) ? categoryTotals.zipCount : zipCounts.length;
  const hasLiveSummary = Number.isInteger(summary?.total_count);
  const countText = !hasLiveSummary && zipCount === 0
    ? copy("map_live_unavailable")
    : countCopy("map_sick_count_one", "map_sick_count", sickCount, { sick: sickCount });
  const metric = mapLabMagnitudeMetric();
  const areaWord = copy(mapLabShadeLevel() === "county" ? "map_area_county" : "map_area_zip");
  const drillHint = mapLabShadeLevel() === "county" ? copy("map_drill_hint") : "";
  let shadingNote;
  if (mapLabDotsVisible()) {
    shadingNote = copy("map_dots_sick_note");
  } else if (metric === "none") {
    shadingNote = formatCopy("map_shading_off_note", { area: areaWord });
  } else if (metric === "flat") {
    shadingNote = formatCopy("map_shading_flat_note", { area: areaWord, drill: drillHint });
  } else {
    shadingNote = formatCopy("map_shading_metric_note", {
      area: areaWord,
      drill: drillHint,
      metric: copy(MAP_LAB_METRIC_NOTE_LABEL_KEYS[metric]),
    });
  }
  const symptoms = mapLabActiveSymptoms();
  if (symptoms.length) {
    shadingNote = formatCopy(symptoms.length > 1 ? "map_showing_symptoms" : "map_showing_symptom", {
      symptom: symptomListLabel(symptoms, state.lang),
      note: shadingNote,
    });
  }
  const noteText = totalCount === null
    ? formatCopy("map_waiting_live_detail", { api: apiOrigin() })
    : formatCopy(zipCount === 1 ? "map_total_summary_one_zip" : "map_total_summary_many_zips", {
      total: allReportCount,
      people: categoryTotals.people || 0,
      animals: categoryTotals.animals || 0,
      environment: categoryTotals.environment || 0,
      zipCount,
      period: reportMapTimePeriodText(),
      note: shadingNote,
    });
  const count = document.getElementById("mapReportCount");
  const note = document.getElementById("mapNote");
  if (count) count.textContent = countText;
  if (note) note.textContent = noteText;
  document.querySelectorAll("[data-mobile-map-count]").forEach((node) => {
    node.textContent = countText;
  });
  document.querySelectorAll("[data-mobile-map-note]").forEach((node) => {
    node.textContent = noteText;
  });
}


export function mapDebugSnapshot() {
  const summary = state.communityReportSummary;
  return {
    liveRpc: {
      method: "read.community_report_count",
      apiOrigin: apiOrigin(),
      result: summary,
      totalCount: Number.isInteger(summary?.total_count) ? summary.total_count : null,
      sickCount: Number.isInteger(summary?.sick_count) ? summary.sick_count : null,
      zipCounts: reportCountsByZip(),
      countyCounts: Array.isArray(summary?.county_counts) ? summary.county_counts : [],
    },
    timeWindow: {
      startDate: state.mapTimeStartDate,
      selectedDate: state.mapTimeSelectedDate,
      windowStartDate: state.mapTimeWindowStartDate,
      windowEndDate: state.mapTimeWindowEndDate,
      baseGeneration: latestMapLabBaseData?.baseGeneration ?? null,
      rawRecords: Array.isArray(state.mapLabRecords) ? state.mapLabRecords.length : 0,
      filteredRecords: Array.isArray(state.mapLabFilteredRecords) ? state.mapLabFilteredRecords.length : 0,
    },
    dotGeoJson: {
      source: "ZIP centroids and report pins inside the shared dynamic GeoJSON source.",
      sourceName: MAP_LAB_DYNAMIC_SOURCE_ID,
    },
    zipGeometry: {
      source: "Static Census TIGERweb 2020 Census ZIP Code Tabulation Areas GeoJSON",
      localUrl: LOCAL_ARIZONA_ZCTAS_URL,
    },
    countyGeometry: {
      source: "Static Census county polygons; ZIP→county via zip-county.js crosswalk",
      localUrl: LOCAL_ARIZONA_COUNTIES_URL,
      shadeLevel: mapLabShadeLevel(),
      drillCounty: mapLabDrillCounty() || null,
    },
    mapbox: {
      fillLayer: "arizona-zip-fills",
      boundaryLayer: "arizona-zip-boundaries",
      boundarySource: `${MAP_LAB_DYNAMIC_SOURCE_ID} GeoJSON source`,
      dotLayer: "community-report-dot-points",
      dotSource: `${MAP_LAB_DYNAMIC_SOURCE_ID} GeoJSON source`,
      labelLayer: "arizona-zip-labels",
      countyFillLayer: "arizona-county-fills",
      countyBoundaryLayer: "arizona-county-boundaries",
      countyLabelLayer: "arizona-county-labels",
      countySource: `${MAP_LAB_DYNAMIC_SOURCE_ID} GeoJSON source`,
    },
  };
}


export function updateMapDebugPanels() {
  document.querySelectorAll(".map-data-debug").forEach((node) => node.remove());
  if (!DEBUG_MAP) return;

  const snapshot = mapDebugSnapshot();
  document.querySelectorAll(".front-door-map-panel, .map-panel, .mobile-map-panel").forEach((panel) => {
    const debug = document.createElement("details");
    debug.className = "map-debug map-data-debug";
    debug.open = true;
    debug.innerHTML = `
      <summary>Map data debug</summary>
      <dl>
        <div><dt>Live RPC</dt><dd>read.community_report_count</dd></div>
        <div><dt>Total reports</dt><dd>${snapshot.liveRpc.totalCount ?? "unavailable"}</dd></div>
        <div><dt>Sick reports</dt><dd>${snapshot.liveRpc.sickCount ?? "unavailable"}</dd></div>
        <div><dt>ZIP counts</dt><dd>${snapshot.liveRpc.zipCounts.length} count row${snapshot.liveRpc.zipCounts.length === 1 ? "" : "s"} joined by ZIP code</dd></div>
        <div><dt>Geometry source</dt><dd>Static Census TIGERweb ZCTA polygons</dd></div>
        <div><dt>Console</dt><dd>window.__communityReportMapDebug</dd></div>
      </dl>
    `;
    panel.append(debug);
  });
}


export function exposeMapDebugApi() {
  window.__communityReportMapDebug = {
    snapshot: mapDebugSnapshot,
    maps: mapInstances,
    queryRenderedFeatures(mapId = "reportMap") {
      const map = mapInstances.get(mapId);
      if (!map) return [];
      return map.queryRenderedFeatures().map((feature) => ({
        layer: feature.layer?.id,
        source: feature.source,
        sourceLayer: feature.sourceLayer,
        properties: feature.properties,
      }));
    },
  };
}


export function logMapDebugSnapshot(mapId) {
  if (!DEBUG_MAP) return;
  const snapshot = mapDebugSnapshot();
  console.info(`[community-report] ${mapId} live RPC data`, snapshot.liveRpc);
  console.info("[community-report] ZIP geometry source", snapshot.zipGeometry);
}
